import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {AbstractWidget} from '../../../../../../models/cms/widget/abstract-widget';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {WidgetService} from '../../../../../../services/cms/widget.service';

import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {AuthService} from '../../../../../../services/auth/auth.service';
import {InputSwitchComponent} from '../../../../../layout/input-switch/input-switch.component';
import {
  ProductPrioritySelectorComponent
} from '../../../../../layout/product-priority-selector/product-priority-selector.component';
import {forkJoin} from 'rxjs';
import {SiteService} from '../../../../../../services/site/site.service';
import {Site} from '../../../../../../models/site/site';
import {ThemeSchema} from '../../../../../../models/cms/widget/theme/theme-schema';
import {ThemeSelectorComponent} from '../../../../../layout/cms/widget/theme-selector/theme-selector.component';
import {WidgetViewerComponent} from '../../../../../layout/cms/widget/widget-viewer/widget-viewer.component';
import {Scope} from '../../../../../../constants/scope';
import {TranslationDomainService} from '../../../../../../services/translation-domain/translation-domain.service';
import {TranslationDomain} from '../../../../../../models/translation/translation-domain';

@Component({
  selector: 'app-widget-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    InputSwitchComponent,
    ThemeSelectorComponent,
    WidgetViewerComponent,
    ProductPrioritySelectorComponent
],
  templateUrl: './widget-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './widget-form.component.scss'
})
@IsGranted(['WIDGET_VIEW'])
@MetaTitle('Widget Form')
export class WidgetFormComponent implements OnInit {
  id: number | null = null;
  loaded = false;
  widget: AbstractWidget | null = null;
  sites: Site[] = [];
  translationDomains: TranslationDomain[] = [];
  themeSchema: ThemeSchema | null = null;
  theme: any = {};

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    siteId: new FormControl<number | null>(null, Validators.required),
    enabled: new FormControl(true),
    scope: new FormControl(Scope.PRIVATE, Validators.required),
    productPriority: new FormControl<string | null>(null),
    // transDefaultDomainId: new FormControl<number | null>(null),
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private siteService: SiteService,
    private widgetService: WidgetService,
    private translationDomainService: TranslationDomainService,
  ) {
  }

  ngOnInit() {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.loader.startLoading();

    forkJoin([
      this.siteService.findAll({sort: 'name'}),
      this.translationDomainService.findAll(),
    ]).subscribe(([sites, translationDomains]) => {
      this.sites = sites;
      this.translationDomains = translationDomains;
    });

    this.refresh();
  }

  refresh(): void {
    if (this.id) {
      this.widgetService.findOneById(this.id, {groups: 'site,theme'}).subscribe(widget => {
        this.widget = widget;

        this.form.patchValue({
          name: this.widget.name,
          siteId: this.widget.site?.id,
          enabled: this.widget.enabled,
          scope: this.widget.scope,
          productPriority: this.widget.productPriority,
          // transDefaultDomainId: this.widget.transDefaultDomain?.id ?? null,
        });

        this.theme = this.widget.theme ?? {};

        this.refreshTheme();

        this.loader.stopLoading();
        this.loaded = true;
      });
    } else {
      this.router.navigateByUrl('/promotion-tools/cms/widgets').then();
    }
  }

  refreshTheme(): void {
    const type = this.widget?.type ?? null;
    if (type) {
      this.widgetService.getThemeSchema(type).subscribe(themeSchema => {
        this.themeSchema = themeSchema;
      });
    }
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    let params: any = this.form.value;

    params.theme = this.theme;

    for (const property in this.widget?.options) {
      params[property] = this.widget?.options[property];
    }

    this.loader.startLoading();

    if (this.widget?.id) {
      this.widgetService.update(this.widget.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Widget has been modified.');
        this.refresh();
      });
    }
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_WIDGET_WRITE']);
  }
}
